import React, {useEffect, useRef, useState} from 'react';
import {showcase} from './content';

const INTERVAL = 6000;

// Desktop: feature list on the left drives a cross-fading screenshot stage.
// Mobile: a swipeable snap carousel.
function Showcase() {
    const [active, setActive] = useState(0);
    const [paused, setPaused] = useState(false);
    const [inView, setInView] = useState(false);
    const sectionRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const el = sectionRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') return;
        const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {threshold: 0.3});
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (paused || !inView) return;
        const id = window.setTimeout(() => setActive((a) => (a + 1) % showcase.length), INTERVAL);
        return () => window.clearTimeout(id);
    }, [active, paused, inView]);

    const running = !paused && inView;

    return (
        <div ref={sectionRef}>
            {/* Desktop */}
            <div
                className="hidden lg:grid grid-cols-[1fr_auto] gap-16 items-center"
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
            >
                <ol className="flex flex-col gap-2">
                    {showcase.map((item, i) => {
                        const isActive = i === active;
                        return (
                            <li key={item.tag}>
                                <button
                                    type="button"
                                    onClick={() => setActive(i)}
                                    className={`relative w-full text-left rounded-2xl px-6 py-5 transition-all duration-300 overflow-hidden ${
                                        isActive ? 'bg-surface shadow-lg shadow-brand/10 ring-1 ring-border' : 'hover:bg-surface/60'
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <span className={`text-xs font-bold tabular-nums transition-colors ${isActive ? 'text-brand' : 'text-soft/60'}`}>
                                            0{i + 1}
                                        </span>
                                        <span className={`text-xs font-bold uppercase tracking-wider transition-colors ${isActive ? 'text-brand' : 'text-soft'}`}>
                                            {item.tag}
                                        </span>
                                    </div>
                                    <h3 className={`mt-1 text-xl font-bold tracking-tight transition-colors ${isActive ? 'text-text' : 'text-text/60'}`}>
                                        {item.title}
                                    </h3>
                                    <div className={`grid transition-all duration-500 ${isActive ? 'grid-rows-[1fr] opacity-100 mt-2' : 'grid-rows-[0fr] opacity-0'}`}>
                                        <p className="overflow-hidden text-soft leading-relaxed">{item.description}</p>
                                    </div>
                                    {isActive && (
                                        <span className="absolute left-0 bottom-0 h-0.5 w-full bg-brand-light">
                                            <span
                                                key={`${active}-${running}`}
                                                className="block h-full bg-brand origin-left"
                                                style={{
                                                    animation: running ? `progress ${INTERVAL}ms linear forwards` : 'none',
                                                    transform: running ? undefined : 'scaleX(0)',
                                                }}
                                            />
                                        </span>
                                    )}
                                </button>
                            </li>
                        );
                    })}
                </ol>

                <div className="relative w-[340px] h-[604px]">
                    <div className="absolute -inset-10 rounded-full bg-brand/20 blur-3xl"/>
                    {showcase.map((item, i) => {
                        const offset = (i - active + showcase.length) % showcase.length;
                        // Current card on top; the next two peek out behind it.
                        const style: React.CSSProperties =
                            offset === 0 ? {transform: 'translateX(0) rotate(0deg) scale(1)', opacity: 1, zIndex: 30}
                                : offset === 1 ? {transform: 'translateX(36px) rotate(5deg) scale(0.94)', opacity: 0.55, zIndex: 20}
                                    : offset === 2 ? {transform: 'translateX(64px) rotate(9deg) scale(0.88)', opacity: 0.25, zIndex: 10}
                                        : {transform: 'translateX(-40px) rotate(-6deg) scale(0.92)', opacity: 0, zIndex: 0};
                        return (
                            <img
                                key={item.tag}
                                src={item.image}
                                alt={`${item.tag} screen`}
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover rounded-[2rem] shadow-2xl shadow-brand-dark/30 transition-all duration-700 ease-[cubic-bezier(.2,.8,.2,1)]"
                                style={style}
                            />
                        );
                    })}
                </div>
            </div>

            {/* Mobile */}
            <div className="lg:hidden -mx-4 sm:-mx-6">
                <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory px-4 sm:px-6 pb-6 no-scrollbar">
                    {showcase.map((item, i) => (
                        <figure key={item.tag} className="snap-center shrink-0 w-[78%] sm:w-[46%]">
                            <img
                                src={item.image}
                                alt={`${item.tag} screen`}
                                loading="lazy"
                                className="w-full aspect-[9/16] object-cover rounded-[1.75rem] shadow-xl shadow-brand-dark/20"
                            />
                            <figcaption className="mt-4 px-1">
                                <span className="text-xs font-bold uppercase tracking-wider text-brand">0{i + 1} · {item.tag}</span>
                                <h3 className="mt-1 text-lg font-bold text-text">{item.title}</h3>
                                <p className="mt-1 text-sm text-soft leading-relaxed">{item.description}</p>
                            </figcaption>
                        </figure>
                    ))}
                </div>
                <p className="text-center text-xs text-soft">Swipe to explore →</p>
            </div>
        </div>
    );
}

export default Showcase;
